import { useState, useEffect } from 'react';
import { churchConfig as configApi } from '../api/endpoints';
import { applyPreferences } from '../utils/preferences';
import { parseOptionLists } from '../utils/optionLists';
import type { ChurchConfig } from '../types';

// The church's name, address and logo appear on every certificate, payslip and
// handover document, and the same row carries the parish-wide settings. There
// is one row on the server, so it is fetched once and shared by every caller
// in the session rather than re-requested per document.

const FALLBACK: ChurchConfig = {
  churchName: 'CSI Church', address: '', contactNo: '', logoBase64: '',
  themeColor: '', documentUploads: true
};

// The brand colour is needed on first paint, before the config request can
// return, or the whole app would flash purple and then re-colour. A copy is
// kept in localStorage purely as a paint-time hint; the server stays the
// source of truth and overwrites it on every load.
const BRAND_KEY = 'church_erp_brand';

export const getCachedBrand = (): string => {
  try {
    return localStorage.getItem(BRAND_KEY) || '';
  } catch {
    return '';
  }
};

const rememberBrand = (colour?: string) => {
  try {
    if (colour) localStorage.setItem(BRAND_KEY, colour);
    else localStorage.removeItem(BRAND_KEY);
  } catch {
    /* private browsing — the colour still applies for this session */
  }
};

let cache: ChurchConfig | null = null;
let inFlight: Promise<ChurchConfig> | null = null;
// True once a real server answer has landed (even a null "not configured
// yet"), as opposed to the fallback used when the server is unreachable.
let loadedFromServer = false;
const subscribers = new Set<(c: ChurchConfig) => void>();

const publish = (data: ChurchConfig) => {
  cache = data;
  rememberBrand(data.themeColor);
  applyPreferences(data);
  subscribers.forEach((fn) => fn(data));
};

const load = (): Promise<ChurchConfig> => {
  if (cache) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = configApi.get()
      // Unconfigured churches return null; an unreachable server must not stop
      // a certificate from printing, so both fall back to the default name.
      .then((data) => { loadedFromServer = true; return data || FALLBACK; })
      .catch(() => FALLBACK)
      .then((data) => {
        inFlight = null;
        publish(data);
        return data;
      });
  }
  return inFlight;
};

/** Whether the cached configuration came from the server rather than the fallback. */
export const hasServerConfig = (): boolean => loadedFromServer;

/** Drops the cache so the next read re-fetches — call after saving Settings. */
export const invalidateChurchConfig = (next?: ChurchConfig) => {
  if (next) {
    loadedFromServer = true;
    publish(next);
  } else {
    cache = null;
    load();
  }
};

/**
 * Saves one dropdown list. The shared cache is updated first so every open
 * form sees the new value at once; the server answer then replaces it. A
 * failed save keeps the optimistic list for this session and reports back so
 * the caller can tell the user.
 */
export const saveOptionList = (key: string, values: string[]): Promise<boolean> => {
  const current = cache || FALLBACK;
  const lists = parseOptionLists(current.optionLists);
  lists[key] = values;
  publish({ ...current, optionLists: JSON.stringify(lists) });

  return configApi.saveOptionList(key, values)
    .then((saved) => {
      if (saved) { loadedFromServer = true; publish(saved); }
      return true;
    })
    .catch(() => false);
};

export const useChurchConfig = (): ChurchConfig => {
  const [config, setConfig] = useState<ChurchConfig>(
    cache || { ...FALLBACK, themeColor: getCachedBrand() }
  );

  useEffect(() => {
    let active = true;
    const update = (c: ChurchConfig) => { if (active) setConfig(c); };
    subscribers.add(update);
    load().then(update);
    return () => { active = false; subscribers.delete(update); };
  }, []);

  return config;
};

export default useChurchConfig;
